import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { Star, ShieldCheck } from "lucide-react";
import { publicApi, dateFr } from "@/lib/api";
import { PageShell, Pill } from "@/components/common";

export function PublicProfile() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  useEffect(() => { publicApi.get(`/users/${id}/public`).then((r) => setU(r.data)); }, [id]);
  if (!u) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  return (
    <PageShell eyebrow={u.city || "Membre"} title={u.company_name || u.display_name}>
      <div className="flex gap-3 items-center mb-8 flex-wrap" data-testid="public-profile">
        {u.verified && <Pill tone="green"><ShieldCheck className="w-3 h-3 mr-1" />Vérifié</Pill>}
        {u.role === "pro" && <Pill tone="forest">Professionnel</Pill>}
        <span className="text-sm text-stone-600">{u.rating ? `${u.rating}/5 (${u.reviews_count} avis)` : "Pas encore d'avis"} · {u.active_listings} annonce(s) active(s)</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {u.reviews.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-stone-200 p-5">
            <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />)}</div>
            <p className="text-sm mt-2">{r.comment || "—"}</p><p className="text-xs text-stone-500 mt-1">{r.author_name} · {dateFr(r.created_at)}</p>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

function AuthLayout({ children }) {
  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative"><img src="/img/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-forest/40" /><p className="relative mt-auto p-12 font-serif text-5xl text-sand leading-tight">Donnez une seconde vie<br />à vos objets.</p></div>
      <div className="flex flex-col items-center justify-center p-6 gap-6"><img src="/img/logo-full.png" alt="labradedie.fr" className="w-64" data-testid="auth-logo" />{children}</div>
    </div>
  );
}

export const SignInPage = () => <AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthLayout>;
export const SignUpPage = () => <AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>;

const LEGAL = {
  cgv: ["Conditions générales (CGU/CGV)", [
    "Document provisoire à faire valider par un juriste avant ouverture publique.",
    "Publication gratuite. Pour un achat, l'acheteur paie le prix de l'objet, une commission de service de 5 % et, le cas échéant, la livraison. Pour un don, le demandeur paie des frais forfaitaires de 2,90 € et, le cas échéant, la livraison. Le vendeur et le donneur ne paient aucun frais.",
    "Le montant payé est conservé par la plateforme jusqu'à validation de la réception par l'acheteur, puis crédité sur le porte-monnaie du vendeur. Le solde du porte-monnaie peut servir à payer des achats sur labradedie.fr.",
    "Les dons sont strictement gratuits : toute demande de contrepartie financière est interdite et peut entraîner la suspension du compte.",
    "Le paiement est opéré par Stripe. labradedie.fr ne stocke aucun numéro de carte. Les fonds sont libérés au vendeur après confirmation de la remise.",
    "Livraison : service d'intermédiation actuellement en simulation. Aucune collecte immédiate ni indemnisation n'est promise sans accord contractuel avec un transporteur.",
    "Remboursements et indemnisations : selon conditions contractuelles explicites, traités par l'administration via les litiges.",
  ]],
  confidentialite: ["Politique de confidentialité", [
    "Document provisoire : base légale, durées de conservation et sous-traitants à documenter (RGPD).",
    "Les adresses exactes ne sont jamais publiques : elles sont communiquées uniquement aux parties autorisées (réservation acceptée, commande payée, prestataire retenu).",
    "Sous-traitants envisagés : authentification (Clerk), paiement (Stripe), stockage de photos, hébergement.",
    "Vous pouvez exporter vos données ou demander leur suppression depuis Mon compte › Confidentialité.",
  ]],
  interdits: ["Objets interdits", [
    "Liste indicative à définir juridiquement : armes et munitions, substances dangereuses ou illicites, médicaments, contrefaçons et répliques, objets volés, déchets dangereux (amiante, produits chimiques).",
    "Les annonces contenant ces termes ou des liens externes sont automatiquement placées en modération.",
  ]],
};

export function Legal() {
  const { page } = useParams();
  const [title, paras] = LEGAL[page] || LEGAL.cgv;
  return <PageShell eyebrow="Informations légales" title={title}><div className="max-w-2xl space-y-4 text-stone-700" data-testid="legal-content">{paras.map((p) => <p key={p}>{p}</p>)}</div></PageShell>;
}
